import {
  type ColumnType,
  type ColumnsType,
  Loading,
  LockIcon,
  Skeleton,
  type SorterResult,
  Space,
  Table,
  type TableProps,
  Text,
} from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { formatOwnershipRatioPercentage } from '@/features/mold/utils';
import {
  type RequestProjectsParams,
  useProjects,
} from '@/features/project/_hooks/project';
import { useHandle403ErrorWhenSearching as useHandle403ErrorWhenSearchingForPS } from '@/features/public-site/hooks';
import { useLocaledAmount } from '@/providers';
import {
  type FormatValueByUnitOptions,
  formatFraction,
  formatValueByUnit,
  handleZeroValueWithNegativeCount,
  isPS,
  lowerCamelStringToSnake,
} from '@/utils';
import { ReviewTypeLabelModifier } from '@/utils/enum';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectsPageSearchQueryStates } from '../../_hooks/search-projects/search-query-states';

import {
  BusinessFormLabelModifier,
  CandidateReasonOfInspectionByExternalExpertModifier,
  InspectionEvaluationRsModel,
  OperationFormLabelModifier,
} from '../_models/project';
import {
  ProjectTableColumnLabelModifier,
  PublishedLabelModifier,
} from '../_models/project-filter';
import type { ProjectListElementModel } from '../_models/projects';
import { BudgetAmountGraph } from './BudgetAmountGraph';

const TABLE_NAME_WIDTH = 380;

type Project = z.infer<typeof ProjectListElementModel>;

type Props = {
  policyId?: string;
  columnsSettings: string[];
  tableActionsColumn: ColumnType<Project>;
  pageSize?: number;
  scrollToTop?: () => void;
  requestParams: RequestProjectsParams;
  setSorting: (sorting: string) => void;
};

const SkeletonTable: FC = () => {
  const columnsNum = 5;
  const rowsNum = 5;

  const columns = [...Array(columnsNum)].map((_, i) => ({
    dataIndex: `col${i}`,
  }));

  const dataSource = [...Array(rowsNum)].map((_, i) => {
    const data = { key: `${i}` };
    for (const { dataIndex } of columns) {
      data[dataIndex] = <Skeleton.Input active size="small" />;
    }

    return data;
  });

  return (
    <Table
      showHeader={false}
      pagination={false}
      rowHoverable={false}
      dataSource={dataSource}
      columns={columns}
      style={{ width: '100%', marginBottom: 40 }}
    />
  );
};

export const ProjectsTable: FC<Props> = (props) => {
  const {
    policyId,
    columnsSettings,
    tableActionsColumn,
    requestParams,
    pageSize,
    scrollToTop,
    setSorting,
  } = props;
  const { t } = useTranslation();
  const { searchQueryStates, setSearchQueryStates } =
    useProjectsPageSearchQueryStates();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = devtoolState?.isDebugMode;

  const [childTablePageNumber, setChildTablePageNumber] = useState('1');

  const getSortOrder = (
    key: keyof Project | string,
  ): 'ascend' | 'descend' | null => {
    const sortingKey: string = lowerCamelStringToSnake(key);
    if (requestParams.ordering === sortingKey) {
      return 'ascend';
    }
    if (requestParams.ordering === `-${sortingKey}`) {
      return 'descend';
    }
    return null;
  };

  const renderAmountText = (
    value: Parameters<typeof formatValueByUnit>[0],
    operation: FormatValueByUnitOptions['operation'] = 'round',
  ) => {
    return formatValueByUnit(value, {
      operation,
      unit,
      showUnitLabel: true,
    });
  };

  const projectsData = useProjects({
    params: {
      ...requestParams,
      policy: policyId,
      ...(policyId ? { page: childTablePageNumber } : []),
      pageSize: searchQueryStates.pageSize.toString(),
    },
  });

  // Be sure to set the width of the header to avoid header title line breaks
  const allColumns: ColumnsType<Project> = [
    {
      title: ProjectTableColumnLabelModifier.name,
      key: 'name',
      fixed: 'left',
      // Note: 検索結果が 0 件の時に左固定のヘッダーセルがずれるのを防ぐため
      width:
        projectsData.data && projectsData.data.results.length > 0
          ? undefined
          : 80,
      render: (_, record) => {
        return (
          <Space
            direction="vertical"
            size="small"
            style={{ maxWidth: TABLE_NAME_WIDTH }}
          >
            <Space>
              {!isPS() && record.isLocked && (
                <LockIcon hint={t('locked-project')} />
              )}
              <Text
                weight="medium"
                text={record.name}
                maxWidth={TABLE_NAME_WIDTH - 20}
              />
            </Space>
            <Text variant="footnote" text={record.overview} maxRows={3} />
          </Space>
        );
      },
    },
    {
      title: ProjectTableColumnLabelModifier.isPublished,
      key: 'isPublished',
      dataIndex: 'isPublished',
      defaultSortOrder: getSortOrder('isPublished'),
      sorter: true,
      width: 100,
      fixed: 'left',
      render: (_, render) => {
        return PublishedLabelModifier[render.isPublished ? 'true' : 'false'];
      },
    },
    ...(requestParams.sheetType === 'RS' &&
    !requestParams.skipAdministrativeReformTeamEvaluation
      ? [
          {
            title:
              ProjectTableColumnLabelModifier.inspectionByExternalExpertReviewType,
            key: 'inspectionByExternalExpertReviewType',
            dataIndex: [
              'inspectionEvaluation',
              'inspectionByExternalExpert',
              'reviewType',
            ],
            width: 128,
            render: (_, record) => {
              const rsInspectionEvaluation =
                InspectionEvaluationRsModel.safeParse(
                  record.inspectionEvaluation,
                );
              if (!rsInspectionEvaluation.success) {
                return null;
              }
              const reviewType =
                rsInspectionEvaluation.data?.inspectionByExternalExpert
                  ?.reviewType;
              return reviewType ? ReviewTypeLabelModifier[reviewType] : null;
            },
          },
        ]
      : []),
    ...(requestParams.sheetType === 'RS' &&
    !requestParams.skipAdministrativeReformTeamEvaluation &&
    !isPS()
      ? ([
          {
            title:
              ProjectTableColumnLabelModifier.candidateReasonsOfInspectionByExternalExpert,
            key: 'candidateReasonsOfInspectionByExternalExpert',
            dataIndex: 'candidateReasonsOfInspectionByExternalExpert',
            width: 184,
            render: (_, { candidateReasonsOfInspectionByExternalExpert }) => {
              const value = candidateReasonsOfInspectionByExternalExpert?.length
                ? candidateReasonsOfInspectionByExternalExpert
                    ?.map(
                      (reason) =>
                        CandidateReasonOfInspectionByExternalExpertModifier[
                          reason
                        ],
                    )
                    .join('\n')
                : null;
              return <Text text={value} variant="body" weight="regular" />;
            },
          },
          {
            title: ProjectTableColumnLabelModifier.lastImplementedFiscalYear,
            key: 'lastImplementedFiscalYear',
            width: 130,
            render: (_, record) => {
              return (
                <Text
                  text={record?.lastImplementedFiscalYear}
                  variant="body"
                  weight="regular"
                />
              );
            },
          },
        ] satisfies ColumnsType<Project>)
      : []),
    {
      title: ProjectTableColumnLabelModifier.ministryName,
      dataIndex: 'ministryName',
      key: 'ministryName',
      width: 200,
      defaultSortOrder: getSortOrder('ministryName'),
      sorter: true,
      render: (_, record) => {
        return (
          <Text text={record.ministryName} variant="body" weight="regular" />
        );
      },
    },
    ...(requestParams.sheetType &&
    ['RS', 'SS'].includes(requestParams.sheetType)
      ? ([
          {
            title: ProjectTableColumnLabelModifier.projectNumber,
            dataIndex: 'projectNumber',
            key: 'projectNumber',
            width: 200,
            render: (_, record) => {
              return (
                <Text
                  text={record.projectNumber}
                  variant="body"
                  weight="regular"
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.budgetData,
            dataIndex: 'budgetData',
            key: 'budgetData',
            width: 200,
            render: (_, record) => {
              return <BudgetAmountGraph budgetAmountData={record.budgetData} />;
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.finalizedAmountForInitialBudget,
            dataIndex: 'finalizedAmountForInitialBudget',
            key: 'finalizedAmountForInitialBudget',
            defaultSortOrder: getSortOrder('finalizedAmountForInitialBudget'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeFinalizedAmountForInitialBudgetCount =
                record.negativeFinalizedAmountForInitialBudgetCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.finalizedAmountForInitialBudget,
                  {
                    negativeCount:
                      negativeFinalizedAmountForInitialBudgetCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.finalizedAmountForInitialBudget);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.organizationLabel,
            dataIndex: 'organizationLabel',
            key: 'organizationLabel',
            render: (_, record) => {
              return record.organization.name;
            },
            width: 500,
            defaultSortOrder: getSortOrder('organizationLabel'),
            sorter: true,
          },
          {
            title: ProjectTableColumnLabelModifier.purpose,
            dataIndex: 'purpose',
            key: 'purpose',
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record.purpose}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.task,
            dataIndex: 'task',
            key: 'task',
            width: 250,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={250}
                  text={record.task}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearExecutedAmounts,
            dataIndex: 'previousYearExecutedAmounts',
            key: 'previousYearExecutedAmounts',
            defaultSortOrder: getSortOrder('previousYearExecutedAmounts'),
            sorter: true,
            width: 280,
            render: (_, { previousYearExecutedAmountData }) => {
              const negativeTotalAmountCount =
                previousYearExecutedAmountData?.negativeTotalAmountCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  previousYearExecutedAmountData?.totalAmount,
                  {
                    negativeCount: negativeTotalAmountCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(
                previousYearExecutedAmountData?.totalAmount,
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearExecutionRate,
            dataIndex: 'previousYearExecutionRate',
            key: 'previousYearExecutionRate',
            defaultSortOrder: getSortOrder('previousYearExecutionRate'),
            sorter: true,
            width: 280,
            render: (_, record) =>
              formatFraction(record.previousYearExecutionRate, 100, {
                maximumFractionDigits: 1,
                useNegativeSign: true,
              }),
          },
          {
            title: ProjectTableColumnLabelModifier.nextYearRequestedAmount,
            dataIndex: 'nextYearRequestedAmount',
            key: 'nextYearRequestedAmount',
            defaultSortOrder: getSortOrder('nextYearRequestedAmount'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeRequestedAmountCount =
                record.negativeRequestedAmountCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(record.requestedAmount, {
                  negativeCount: negativeRequestedAmountCount ?? 0,
                  isDebugMode,
                });
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.requestedAmount);
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.finalizedAmountForSupplementaryBudget,
            dataIndex: 'finalizedAmountForSupplementaryBudget',
            key: 'finalizedAmountForSupplementaryBudget',
            defaultSortOrder: getSortOrder(
              'finalizedAmountForSupplementaryBudget',
            ),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeFinalizedAmountForSupplementaryBudgetCount =
                record.negativeFinalizedAmountForSupplementaryBudgetCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.finalizedAmountForSupplementaryBudget,
                  {
                    negativeCount:
                      negativeFinalizedAmountForSupplementaryBudgetCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(
                record.finalizedAmountForSupplementaryBudget,
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.startYear,
            dataIndex: 'startYear',
            key: 'startYear',
            width: 150,
            defaultSortOrder: getSortOrder('startYear'),
            sorter: true,
            render: (_, record) => {
              return (
                <Text text={record.startYear} variant="body" weight="regular" />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.endYear,
            dataIndex: 'endYear',
            key: 'endYear',
            width: 150,
            defaultSortOrder: getSortOrder('endYear'),
            sorter: true,
            render: (_, record) => {
              return (
                <Text text={record.endYear} variant="body" weight="regular" />
              );
            },
          },
        ] satisfies ColumnsType<Project>)
      : []),
    ...(requestParams.sheetType === 'KS'
      ? ([
          {
            title: ProjectTableColumnLabelModifier.fundOwner,
            dataIndex: 'fundOwner',
            key: 'fundOwner',
            width: 200,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.fundOwner}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.fiscalYearOfCreation,
            dataIndex: 'fiscalYearOfCreation',
            key: 'fiscalYearOfCreation',
            defaultSortOrder: getSortOrder('fiscalYearOfCreation'),
            sorter: true,
            width: 120,
            render: (_, record) => {
              return (
                <Text
                  weight="regular"
                  text={record?.compositionBackgroundsFiscalYearOfCreation}
                />
              );
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.endDateOfNewApplicationReceiving,
            dataIndex: 'endDateOfNewApplicationReceiving',
            key: 'endDateOfNewApplicationReceiving',
            defaultSortOrder: getSortOrder('endDateOfNewApplicationReceiving'),
            sorter: true,
            width: 250,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.scheduledEndEndDateOfNewApplicationReceiving}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.scheduledEndDate,
            dataIndex: 'scheduledEndDate',
            key: 'scheduledEndDate',
            defaultSortOrder: getSortOrder('scheduledEndDate'),
            sorter: true,
            width: 200,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.scheduledEndEndDate}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.administrativeExpenses,
            dataIndex: [
              'fundAdditionalIncomeAndExpenditure',
              'prior',
              'administrativeExpense',
            ],
            defaultSortOrder: getSortOrder('previousYearAdministrativeExpense'),
            sorter: true,
            key: 'previousYearAdministrativeExpense',
            width: 200,
            render: (_, record) => {
              return renderAmountText(record.previousYearAdministrativeExpense);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearTotalIncome,
            dataIndex: 'previousYearTotalIncome',
            key: 'previousYearTotalIncome',
            defaultSortOrder: getSortOrder('previousYearTotalIncome'),
            sorter: true,
            width: 150,
            render: (_, record) => {
              const negativePreviousYearTotalIncomeCount =
                record.negativePreviousYearTotalIncomeCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.previousYearTotalIncome,
                  {
                    negativeCount: negativePreviousYearTotalIncomeCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.previousYearTotalIncome);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearTotalExpense,
            dataIndex: 'previousYearTotalExpense',
            key: 'previousYearTotalExpense',
            defaultSortOrder: getSortOrder('previousYearTotalExpense'),
            sorter: true,
            width: 200,
            render: (_, record) => {
              const negativePreviousYearTotalExpenseCount =
                record.negativePreviousYearTotalExpenseCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.previousYearTotalExpense,
                  {
                    negativeCount: negativePreviousYearTotalExpenseCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.previousYearTotalExpense);
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.amountReturnedToTheNationalTreasury,
            dataIndex: 'previousYearAmountReturnedToTheNationalTreasury',
            defaultSortOrder: getSortOrder(
              'previousYearAmountReturnedToTheNationalTreasury',
            ),
            sorter: true,
            key: 'previousYearAmountReturnedToTheNationalTreasury',
            width: 200,
            render: (_, record) => {
              return renderAmountText(
                record.previousYearAmountReturnedToTheNationalTreasury,
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.fundSheetNumber,
            dataIndex: 'fundSheetNumber',
            key: 'fundSheetNumber',
            defaultSortOrder: getSortOrder('fundSheetNumber'),
            sorter: true,
            width: 120,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.fundSheetNumber}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.fundSheetBranchNumber,
            dataIndex: 'fundSheetBranchNumber',
            key: 'fundSheetBranchNumber',
            width: 100,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.fundSheetBranchNumber}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.operationForms,
            dataIndex: 'operationForms',
            key: 'operationForms',
            width: 150,
            render: (_, record) => {
              const operationForms = record?.operationForms
                ?.map((form) => OperationFormLabelModifier[form])
                .join(', ');
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={operationForms}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.businessForms,
            dataIndex: 'businessForms',
            key: 'businessForms',
            width: 150,
            render: (_, record) => {
              const businessForms = record?.businessForms
                ?.map((form) => BusinessFormLabelModifier[form])
                .join(', ');
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={businessForms}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.beginningOfTheFundBalance,
            dataIndex: 'beginningOfTheFundBalance',
            key: 'beginningOfTheFundBalance',
            defaultSortOrder: getSortOrder('beginningOfTheFundBalance'),
            sorter: true,
            width: 150,
            render: (_, record) => {
              return renderAmountText(record.beginningOfTheFundBalance);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.ownershipRatio,
            dataIndex: 'ownershipRatio',
            key: 'ownershipRatio',
            defaultSortOrder: getSortOrder('ownershipRatio'),
            sorter: true,
            render: (_, record) =>
              formatOwnershipRatioPercentage(record.ownershipRatio),
            width: 100,
          },
          {
            title:
              ProjectTableColumnLabelModifier.previousYearAdministrativeExpenseRate,
            dataIndex: 'previousYearAdministrativeExpenseRate',
            key: 'previousYearAdministrativeExpenseRate',
            defaultSortOrder: getSortOrder(
              'previousYearAdministrativeExpenseRate',
            ),
            sorter: true,
            width: 200,
            render: (_, record) =>
              formatFraction(
                record.previousYearAdministrativeExpenseRate,
                100,
                {
                  maximumFractionDigits: 1,
                  useNegativeSign: true,
                },
              ),
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearDivergenceRate,
            dataIndex: 'previousYearDivergenceRate',
            key: 'previousYearDivergenceRate',
            defaultSortOrder: getSortOrder('previousYearDivergenceRate'),
            sorter: true,
            width: 200,
            render: (_, record) =>
              formatFraction(record.previousYearDivergenceRate, 100, {
                maximumFractionDigits: 1,
                useNegativeSign: true,
              }),
          },
        ] satisfies ColumnsType<Project>)
      : []),
    {
      title: ProjectTableColumnLabelModifier.mainPayment,
      dataIndex: 'mainPayment',
      key: 'mainPayment',
      width: 380,
      render: (_, record) => {
        return (
          <Space direction="vertical" size="small">
            <Space>
              <Text weight="medium" text={record.paymentData.name} />
            </Space>
            <Text
              variant="footnote"
              color="secondary"
              text={record.paymentData.corporateNumber}
            />
          </Space>
        );
      },
    },
    {
      title: ProjectTableColumnLabelModifier.paymentAmount,
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      defaultSortOrder: getSortOrder('paymentAmount'),
      sorter: true,
      width: 200,
      render: (_, record) => {
        const negativeAmountCount = record.paymentData.negativeAmountCount;

        const { isAggregateValueZero, fallbackValue } =
          handleZeroValueWithNegativeCount(record.paymentData.amount, {
            negativeCount: negativeAmountCount ?? 0,
            isDebugMode,
          });
        if (isAggregateValueZero) {
          return fallbackValue;
        }
        return renderAmountText(record.paymentData.amount);
      },
    },
  ];
  const columns = columnsSettings.flatMap(
    (columnsSetting) =>
      allColumns.find((column) => column.key === columnsSetting) ?? [],
  );

  const handleSingleSorter = useCallback(
    (sorter: SorterResult<Project>) => {
      if (sorter.order && sorter.columnKey) {
        const prefix = sorter.order === 'descend' ? '-' : '';
        const orderingKey = lowerCamelStringToSnake(
          sorter.columnKey.toString(),
        );
        const ordering = (() => {
          switch (sorter.columnKey) {
            case 'ministryName':
              return `${prefix}${lowerCamelStringToSnake('ministry')}`;
            case 'previousYearExecutedAmounts':
              return `${prefix}${lowerCamelStringToSnake(
                'previousYearExecutedAmountDataTotalAmount',
              )}`;
            case 'administrativeExpenses':
              return `${prefix}${lowerCamelStringToSnake(
                'administrativeExpense',
              )}`;
            default:
              return `${prefix}${orderingKey}`;
          }
        })();
        if (policyId) {
          setSorting(ordering);
        } else {
          setSearchQueryStates((prev) => ({
            ...prev,
            page: 1,
            ordering,
          }));
        }
      } else {
        if (policyId) {
          setSorting('');
        } else {
          setSearchQueryStates((prev) => ({
            ...prev,
            page: 1,
            ordering: null,
          }));
        }
      }
    },
    [policyId, setSorting, setSearchQueryStates],
  );

  useHandle403ErrorWhenSearchingForPS(projectsData);

  const tableProps = useMemo<TableProps<Project>>(
    () => ({
      columns: [...columns, tableActionsColumn],
      dataSource: projectsData.data?.results,
      rowKey: 'id',
      pagination: {
        current: policyId
          ? Number(childTablePageNumber)
          : Number(requestParams.page),
        total: projectsData.data?.count,
        showSizeChanger: false,
        pageSize: Number(pageSize),
      },
      scroll: { x: 'max-content' },
      style: { width: '100%' },
      onChange: (pagination, _, sorter, extra) => {
        switch (extra.action) {
          case 'paginate':
            if (pagination.current) {
              if (policyId) {
                setChildTablePageNumber(pagination.current.toString());
                scrollToTop?.();
              } else {
                setSearchQueryStates((prev) => ({
                  ...prev,
                  page: pagination.current,
                }));
              }
            }
            break;
          case 'sort':
            invariant(!Array.isArray(sorter), t('unreachable-code'));
            handleSingleSorter(sorter);
            break;
          default:
        }
      },
    }),
    [
      columns,
      tableActionsColumn,
      projectsData.data?.results,
      projectsData.data?.count,
      pageSize,
      setSearchQueryStates,
      handleSingleSorter,
      childTablePageNumber,
      policyId,
      scrollToTop,
      requestParams,
      t,
    ],
  );

  if (projectsData.isLoading || !projectsData.data) {
    return isPS() ? <SkeletonTable /> : <Loading />;
  }

  return (
    <Table
      aria-label="projects-table"
      key={JSON.stringify(columns)}
      {...tableProps}
    />
  );
};
